<template>
  <div class="connector-edit-root" v-loader.opacity.tab="loading">
    <div class="title-box">
      <h2 class="title">{{ partner.label }}</h2>
    </div>

    <div class="clinic-connector-box scrollbar">
      <div class="row">
        <div class="col-lg-8">
          <div class="form-floating mb-3">
            <input type="text" class="form-control" id="connectorName" v-model="form.name" :placeholder="$t('connector.name')" data-test="inputConnectorName">
            <label for="connectorName">{{ $t('connector.name') }}</label>
          </div>
        </div>
        <div class="col-lg-4 enabled-col">
          <div class="form-check form-switch enabled-switch">
            <label class="form-check-label" for="connectorEnabled">{{ $t('integration.enabled') }}</label>
            <input class="form-check-input" type="checkbox" role="switch" id="connectorEnabled" v-model="form.enabled" data-test="checkConnectorEnabled">
          </div>
        </div>
      </div>

      <section v-if="catalog">
        <h3 class="sub-title">{{ $t('connector.analyses') }}</h3>
        <div class="analyses-box">
          <label class="form-check analysis-item" v-for="a in catalog.analyses" :key="a">
            <input class="form-check-input" type="checkbox" :value="a" v-model="form.analyses">
            <span>{{ a }}</span>
          </label>
        </div>
      </section>

      <section v-if="catalog">
        <h3 class="sub-title">{{ $t('connector.mapping') }}</h3>
        <div class="mapping-head">
          <span class="col-field">{{ $t('connector.field') }}</span>
          <span class="col-source">{{ $t('connector.source') }}</span>
          <span class="col-transform">{{ $t('connector.transform') }}</span>
        </div>
        <div class="mapping-row" v-for="field in mappableFields" :key="field.key">
          <div class="col-field">
            <span class="field-key">{{ field.label }}</span>
            <span v-if="field.required" class="field-required">*</span>
          </div>
          <div class="col-source">
            <v-select
              class="v-select form"
              :options="sourceOptions"
              :reduce="x => x.value"
              :clearable="!field.required"
              :placeholder="$t('connector.selectSource')"
              v-model="form.fieldMap[field.key].source">
            </v-select>
          </div>
          <div class="col-transform">
            <span v-if="field.transform" class="transform-tag">{{ field.transform }}</span>
            <span v-else>&mdash;</span>
          </div>
        </div>
      </section>

      <section v-if="catalog">
        <h3 class="sub-title">{{ $t('connector.testWithExam') }}</h3>
        <div class="row">
          <div class="col-lg-6">
            <div class="form-floating mb-3">
              <input type="text" class="form-control" id="testExamId" v-model="test.examId" :placeholder="$t('connector.examId')" data-test="inputTestExamId">
              <label for="testExamId">{{ $t('connector.examId') }}</label>
            </div>
          </div>
          <div class="col-lg-6">
            <div class="form-floating mb-3">
              <input type="text" class="form-control" id="testExamDataUuid" v-model="test.examDataUuid" :placeholder="$t('connector.examDataUuid')" data-test="inputTestExamDataUuid">
              <label for="testExamDataUuid">{{ $t('connector.examDataUuid') }}</label>
            </div>
          </div>
        </div>
        <button class="btn btn-outline-secondary test-btn" type="button" :disabled="!test.examId && !test.examDataUuid" @click="runPreview" data-test="btnRunPreview">
          <i class="fa fa-flask"></i>
          <span>{{ $t('connector.runTest') }}</span>
        </button>
        <div class="preview-box" v-if="test.payload">
          <label>{{ $t('connector.payload') }}</label>
          <pre>{{ test.payloadText }}</pre>
        </div>
      </section>
    </div>

    <div class="action-bar">
      <button class="btn btn-success" type="button" @click="doSave" data-test="btnSaveConnector">
        <span>{{ $t('save') }}</span>
      </button>
      <button class="btn btn-secondary" type="button" @click="$emit('cancel-edit')" data-test="btnCancelConnector">
        <span>{{ $t('cancel') }}</span>
      </button>
      <button v-if="form.uuid" class="btn btn-outline-danger" type="button" @click="doRemove" data-test="btnRemoveConnector">
        <i class="fa fa-ban"></i>
        <span>{{ $t('remove') }}</span>
      </button>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.clinic-connector-box {
  height:                       calc(100dvh - 394px);
  padding:                      10px;
  overflow-x:                   hidden;
  overflow-y:                   scroll;
}

@media (min-width: 992px) {
  .connector-edit-root {
    display:                    flex;
    flex-direction:             column;
    flex:                       1;
    min-height:                 0;
  }

  .clinic-connector-box {
    height:                     auto;
    flex:                       1;
    min-height:                 0;
  }
}

section {
  display:                      flex;
  flex-direction:               column;
  gap:                          12px;
  margin-bottom:                28px;
}

.analyses-box {
  display:                      flex;
  gap:                          14px;
  flex-wrap:                    wrap;

  .analysis-item {
    margin:                     0;
    min-width:                  100px;
    gap:                        10px;
    cursor:                     pointer;

    .form-check-input {
      margin:                   0;
      float:                    none;
      cursor:                   pointer;
    }

    .form-check-input:checked {
      background-color:         var(--Support-Success, #3CB391);
      border-color:             var(--Support-Success, #3CB391);
    }

    span {
      color:                    var(--Brand-Primary);
      font-size:                13px;
      font-weight:              500;
      letter-spacing:           1.5px;
      text-transform:           uppercase;
    }
  }
}

.enabled-col {
  display:                      flex;
  align-items:                  center;
}

.enabled-switch.form-check {
  width:                        100%;
  border:                       none;
  background:                   transparent;
  padding:                      0;
  gap:                          12px;
  justify-content:              flex-start;

  .form-check-input {
    margin:                     0;
    cursor:                     pointer;
  }

  .form-check-input:checked {
    background-color:           var(--Support-Success, #3CB391);
    border-color:               var(--Support-Success, #3CB391);
  }

  .form-check-label {
    color:                      var(--Shades-60-W);
    letter-spacing:             1.5px;
  }
}

.mapping-head,
.mapping-row {
  display:                      grid;
  grid-template-columns:        minmax(180px, 1fr) minmax(240px, 2fr) 110px;
  gap:                          16px;
  align-items:                  center;
}

.mapping-head {
  font-size:                    12px;
  font-weight:                  600;
  color:                        var(--Shades-60-W);
  text-transform:               uppercase;
  letter-spacing:               0.5px;
  padding-bottom:               10px;
  border-bottom:                1px solid var(--Shades-20-Border);
}

.mapping-row {
  padding:                      10px 0;
  border-bottom:                1px solid var(--Shades-20-Border);
}

.col-source {
  --vs-selected-color:                    var(--Brand-Primary);
  --vs-search-input-color:                var(--Brand-Primary);
  --vs-search-input-placeholder-color:    var(--Shades-50);
  --vs-border-color:                      var(--Shades-30-Border);
  --vs-dropdown-bg:                       var(--Shades-BG);
  --vs-dropdown-color:                    var(--Brand-Primary);
  --vs-dropdown-option-color:             var(--Brand-Primary);
  --vs-dropdown-option--active-bg:        var(--Shades-10);
  --vs-dropdown-option--active-color:     var(--Brand-Primary);
}

.field-key {
  font-weight:                  500;
  color:                        var(--Brand-Primary);
}

.field-required {
  color:                        var(--Support-Error);
  margin-left:                  2px;
}

.col-transform {
  color:                        var(--Shades-50);
  font-size:                    13px;
}

.transform-tag {
  font-size:                    11px;
  font-family:                  monospace;
  padding:                      3px 8px;
  border-radius:                4px;
  background:                   var(--Shades-10);
  color:                        var(--Shades-60-W);
}

.test-btn {
  align-self:                   flex-start;
  gap:                          8px;
  padding:                      8px 16px;
  letter-spacing:               1.8px;

  i {
    color:                      var(--Brand-Secondary, #3CB391);
  }

  &:disabled {
    opacity:                    0.5;
    cursor:                     not-allowed;
  }
}

.preview-box {
  margin-top:                   14px;

  label {
    display:                    block;
    color:                      var(--Shades-60-W);
    font-size:                  13px;
    margin-bottom:              6px;
  }

  pre {
    margin:                     0;
    background:                 var(--Shades-10);
    border:                     1px solid var(--Shades-30-Border);
    border-radius:              6px;
    padding:                    12px;
    font-size:                  12px;
    color:                      var(--Brand-Primary);
    overflow-x:                 auto;
  }
}
</style>

<script>
function buildForm(partnerItem, catalog) {
  const integration = partnerItem && partnerItem.integration
  const config = (integration && integration.config) || {}
  const fieldMap = {}
  const fields = (catalog && catalog.fields) || []
  for (const f of fields) {
    if (f.fixed) continue
    const existing = config.fieldMap && config.fieldMap[f.key]
    fieldMap[f.key] = { source: existing && existing.source ? existing.source : (f.defaultSource || '') }
  }
  const analyses = (config.analyses && config.analyses.length)
    ? config.analyses.slice()
    : ((catalog && catalog.analyses) ? catalog.analyses.slice() : [])
  return {
    uuid: integration ? integration.uuid : null,
    name: integration && integration.name ? integration.name : (catalog ? catalog.label : partnerItem.label),
    enabled: integration ? integration.enabled : true,
    analyses,
    fieldMap,
  }
}

export default {
  name: 'ClinicConnectorEdit',
  props: ['clinic_id', 'partner'],
  data: () => ({
    loading: false,
    catalog: null,
    form: { uuid: null, name: '', enabled: true, analyses: [], fieldMap: {} },
    test: { examId: '', examDataUuid: '', payload: null, payloadText: '' },
  }),
  computed: {
    mappableFields() {
      if (!this.catalog || !this.catalog.fields) return []
      return this.catalog.fields.filter(f => !f.fixed)
    },
    sourceOptions() {
      if (!this.catalog || !this.catalog.eyercloudSources) return []
      return this.catalog.eyercloudSources.map(s => ({ value: s.path, label: s.label }))
    },
  },
  mounted() {
    this.fetchCatalog()
  },
  methods: {
    fetchCatalog: async function() {
      this.loading = true
      NProgress.start()
      try {
        let response = await this.$connectorService.getFieldCatalog(this.partner.name)
        this.catalog = response && response.result ? response.result : response
        this.form = buildForm(this.partner, this.catalog)
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'request_error', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    buildFieldMapForSave() {
      const out = {}
      for (const key of Object.keys(this.form.fieldMap)) {
        const source = this.form.fieldMap[key].source
        if (source) out[key] = { source }
      }
      return out
    },
    doSave: async function() {
      if (!this.form.name) {
        this.$message.popup(this, 'connector.nameRequired', 'operationWarning', 'warn')
        return
      }
      const params = {
        clinicId: this.clinic_id,
        integration: {
          uuid: this.form.uuid,
          type: 'connector',
          name: this.form.name,
          enabled: this.form.enabled,
          config: {
            partner: this.partner.name,
            fieldMap: this.buildFieldMapForSave(),
            analyses: this.form.analyses,
          },
        },
      }
      this.loading = true
      NProgress.start()
      try {
        const response = await this.$clinicService.saveIntegration(params)
        const created = response && response.created !== undefined ? response.created : !this.form.uuid
        this.$message.popup(this, created ? 'general.createdSuccess' : 'general.updatedSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, this.form.uuid ? 'general.updatedError' : 'general.createdError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
    runPreview: async function() {
      this.loading = true
      NProgress.start()
      try {
        const result = await this.$connectorService.preview({
          partner: this.partner.name,
          clinicId: this.clinic_id,
          examId: this.test.examId || undefined,
          examDataUuid: this.test.examDataUuid || undefined,
          analyses: this.form.analyses,
          fieldMap: this.buildFieldMapForSave(),
        })
        const payload = result.result ? result.result.payload : null
        this.test.payload = payload
        this.test.payloadText = JSON.stringify(result.result || {}, null, 2)
      } catch (err) {
        console.log(err)
        const data = err && err.response ? err.response.data : null
        if (data && data.errorMessage === 'previewInvalidPayload') {
          this.$message.popup(this, 'connector.previewInvalid', 'operationWarning', 'warn', { params: { detail: data.detail || '' } })
        } else {
          this.$message.popup(this, 'connector.previewFail', 'operationFail', 'error')
        }
      }
      NProgress.done(true)
      this.loading = false
    },
    doRemove: async function() {
      this.loading = true
      NProgress.start()
      try {
        await this.$clinicService.removeIntegration({ clinicId: this.clinic_id, uuid: this.form.uuid })
        this.$message.popup(this, 'integration.removeSuccess', 'operationSuccess', 'success')
        this.$emit('saved')
      } catch (err) {
        console.log(err)
        this.$message.popup(this, 'integration.removeError', 'operationFail', 'error')
      }
      NProgress.done(true)
      this.loading = false
    },
  },
}
</script>
